import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, CheckCircle, ExternalLink, Copy, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { HMACSecretSetup } from './HMACSecretSetup';

export const ElevenLabsScreeningSetup = () => {
  const webhookUrl = `https://bdvwkygtrettvwiwbxbc.supabase.co/functions/v1/elevenlabs-screening-webhook`;
  const candidateLookupUrl = `https://bdvwkygtrettvwiwbxbc.supabase.co/functions/v1/candidate-lookup`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const setupSteps = [
    {
      title: "Configure ElevenLabs Agent",
      description: "Set up your conversational AI agent with the following settings:",
      details: [
        "Enable phone calling capabilities",
        "Set up voice selection for screening scenarios",
        "Configure conversation flow for candidate screening"
      ]
    },
    {
      title: "Add Webhook URLs",
      description: "Configure these webhooks in your ElevenLabs dashboard:",
      details: [
        `Screening Webhook: ${webhookUrl}`,
        `Candidate Lookup: ${candidateLookupUrl}`
      ]
    },
    {
      title: "Set Conversation Flow",
      description: "Configure your agent to:",
      details: [
        "Greet callers and ask for their name",
        "Call the candidate lookup webhook to find their profile",
        "Conduct personalized screening based on their background",
        "Send completion webhook when call ends"
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Phone className="h-6 w-6 text-blue-600" />
              <div>
                <CardTitle>On-Demand Phone Screening</CardTitle>
                <CardDescription>
                  Allow candidates to call your ElevenLabs number for instant screening
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              Available
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {setupSteps.map((step, index) => (
              <div key={index} className="space-y-3">
                <h4 className="font-medium text-lg">{index + 1}. {step.title}</h4>
                <p className="text-muted-foreground text-sm">{step.description}</p>
                <ul className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-sm">{detail}</span>
                      {detail.includes('http') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(detail.split(': ')[1], 'URL')}
                          className="h-6 px-2"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhook Configuration</CardTitle>
          <CardDescription>Use these URLs in your ElevenLabs agent configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Candidate Lookup Webhook</label>
              <Badge variant="outline" className="text-xs">
                <Shield className="w-3 h-3 mr-1" />
                HMAC Auth
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                {candidateLookupUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(candidateLookupUrl, 'Candidate Lookup URL')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Requires HMAC-SHA256 signature in x-signature header
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Call Completion Webhook</label>
              <Badge variant="outline" className="text-xs">
                <Shield className="w-3 h-3 mr-1" />
                HMAC Auth
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                {webhookUrl}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(webhookUrl, 'Webhook URL')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Requires HMAC-SHA256 signature in x-signature header
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">1</div>
              <div>
                <p className="font-medium">Candidate calls your ElevenLabs number</p>
                <p className="text-muted-foreground">Your agent greets them and asks for their name</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">2</div>
              <div>
                <p className="font-medium">Agent searches your CRM</p>
                <p className="text-muted-foreground">Using the candidate lookup webhook to find their profile</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">3</div>
              <div>
                <p className="font-medium">Personalized screening</p>
                <p className="text-muted-foreground">Questions tailored to their skills and applied positions</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">4</div>
              <div>
                <p className="font-medium">Results in your CRM</p>
                <p className="text-muted-foreground">Transcript and analysis automatically saved to candidate profile</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>First Message Configuration</CardTitle>
          <CardDescription>
            Optimized greeting messages using dynamic variables from candidate lookup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">For Known Candidates (when match_found = true)</h4>
              <div className="relative">
                <textarea
                  readOnly
                  value={`Hello {{candidate_name}}! Thank you for calling Invenio Search Group. I'm Karsen, an AI recruiting assistant who found your profile in our system. I see you have {{experience_years}} years of experience and skills in {{candidate_skills}}, which makes you an excellent fit for our {{applied_position}} opportunities.

I'll be conducting a personalized assessment today to understand your career aspirations and how we can best serve your professional goals. This conversation typically takes 10-15 minutes and will help us match you with the most suitable opportunities.

Let's start - what specifically interests you about the {{applied_position}} role, and what are you looking for in your next career move?`}
                  className="w-full h-32 p-3 text-sm font-mono bg-muted border rounded resize-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(`Hello {{candidate_name}}! Thank you for calling Invenio Search Group. I'm Karsen, an AI recruiting assistant who found your profile in our system. I see you have {{experience_years}} years of experience and skills in {{candidate_skills}}, which makes you an excellent fit for our {{applied_position}} opportunities.

I'll be conducting a personalized assessment today to understand your career aspirations and how we can best serve your professional goals. This conversation typically takes 10-15 minutes and will help us match you with the most suitable opportunities.

Let's start - what specifically interests you about the {{applied_position}} role, and what are you looking for in your next career move?`, 'Known Candidate Message')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">For Unknown Candidates (when match_found = false)</h4>
              <div className="relative">
                <textarea
                  readOnly
                  value={`Hello! Thank you for calling Invenio Search Group. I'm Karsen, an AI recruiting assistant who specializes in executive and professional placements. I don't have your information in our system yet, but I'm excited to learn about your background and discuss how we might help with your career goals.

I'll be conducting a comprehensive preliminary assessment today to understand your experience, skills, and career aspirations. This conversation typically takes 10-15 minutes and will help us determine the most suitable opportunities for someone with your background.

To get started, could you please tell me your full name and what type of role or opportunity brought you to call us today?`}
                  className="w-full h-32 p-3 text-sm font-mono bg-muted border rounded resize-none"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(`Hello! Thank you for calling Invenio Search Group. I'm Karsen, an AI recruiting assistant who specializes in executive and professional placements. I don't have your information in our system yet, but I'm excited to learn about your background and discuss how we might help with your career goals.

I'll be conducting a comprehensive preliminary assessment today to understand your experience, skills, and career aspirations. This conversation typically takes 10-15 minutes and will help us determine the most suitable opportunities for someone with your background.

To get started, could you please tell me your full name and what type of role or opportunity brought you to call us today?`, 'Unknown Candidate Message')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t">
            <h4 className="font-medium">Available Dynamic Variables</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <code className="bg-muted px-2 py-1 rounded text-xs">{`{{candidate_name}}`}</code>
                <p className="text-muted-foreground mt-1">Full name from database</p>
              </div>
              <div>
                <code className="bg-muted px-2 py-1 rounded text-xs">{`{{experience_years}}`}</code>
                <p className="text-muted-foreground mt-1">Years of experience</p>
              </div>
              <div>
                <code className="bg-muted px-2 py-1 rounded text-xs">{`{{candidate_skills}}`}</code>
                <p className="text-muted-foreground mt-1">Comma-separated skills list</p>
              </div>
              <div>
                <code className="bg-muted px-2 py-1 rounded text-xs">{`{{applied_position}}`}</code>
                <p className="text-muted-foreground mt-1">Job title they applied for</p>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-blue-800">
                <strong>Setup Instructions:</strong> Copy the appropriate message above and paste it as your "First Message" in the ElevenLabs Conversational AI dashboard. The variables will be automatically populated by the candidate-lookup webhook.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <HMACSecretSetup />
    </div>
  );
};